import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import type { BookingConfirmationDetails } from '../utils/bookingConfirmationWindow';
import {
  bookingConfirmationDisplayPeopleCount,
  bookingCalendarEventFromConfirmation,
} from '../utils/bookingConfirmationWindow';
import { BookingCalendarExportButtons } from './BookingCalendarExportButtons';
import styles from './BookingConfirmationCard.module.css';

type BookingConfirmationCardProps = {
  details: BookingConfirmationDetails;
  popupBlockedHint?: string;
  onClose?: () => void;
  closeLabel?: string;
};

function SuccessBadgeIcon() {
  return (
    <svg className={styles.successBadgeSvg} viewBox="0 0 48 48" aria-hidden>
      <circle cx="24" cy="24" r="24" fill="currentColor" />
      <path
        d="M14 24.5 20.5 31 34 17.5"
        fill="none"
        stroke="#fff"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BarcodeIcon() {
  return (
    <svg className={styles.barcodeIcon} viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M3 5h2v14H3V5zm4 0h1v14H7V5zm3 0h2v14h-2V5zm4 0h1v14h-1V5zm3 0h3v14h-3V5zm5 0h1v14h-1V5z" />
    </svg>
  );
}

export function BookingConfirmationCard({
  details,
  popupBlockedHint,
  onClose,
  closeLabel,
}: BookingConfirmationCardProps) {
  const [qrOpen, setQrOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const bookingId = details.bookingId.trim();
  const dir = details.language === 'he' ? 'rtl' : 'ltr';
  const peopleCount = bookingConfirmationDisplayPeopleCount(details);

  useEffect(() => {
    setQrOpen(false);
    setQrDataUrl(null);
  }, [bookingId]);

  useEffect(() => {
    if (!qrOpen || !bookingId) {
      return undefined;
    }
    let cancelled = false;
    void QRCode.toDataURL(bookingId, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (!cancelled) {
          setQrDataUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQrDataUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [qrOpen, bookingId]);

  const { labels } = details;
  const calendarEvent = bookingCalendarEventFromConfirmation(details);

  return createPortal(
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={labels.title}
      onClick={() => onClose?.()}
    >
      <section className={styles.card} dir={dir} onClick={(e) => e.stopPropagation()}>
        {onClose ? (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={closeLabel ?? 'Close'}
          >
            ×
          </button>
        ) : null}
        <div className={styles.successBadgeWrap}>
          <SuccessBadgeIcon />
        </div>
        <header className={styles.venueHeader}>
          <p className={styles.assetName}>
            {details.assetName.trim() ||
              (details.venuePending
                ? labels.venueLoading
                : labels.venueUnknown) ||
              '…'}
          </p>
          {details.assetAddress ? (
            <p className={styles.assetAddress}>{details.assetAddress}</p>
          ) : null}
        </header>
        {popupBlockedHint ? <p className={styles.hint}>{popupBlockedHint}</p> : null}
        <h2 className={styles.title}>{labels.title}</h2>
        <p className={styles.subtitle}>{labels.subtitle}</p>
        <dl className={styles.details}>
          <div className={styles.detailRow}>
            <dt>{labels.scheduleLabel}</dt>
            <dd>{details.scheduleText}</dd>
          </div>
          {peopleCount !== null ? (
            <div className={styles.detailRow}>
              <dt>{labels.peopleLabel}</dt>
              <dd>{peopleCount}</dd>
            </div>
          ) : null}
          {details.vehicleSummary?.trim() && labels.vehicleLabel ? (
            <div className={styles.detailRow}>
              <dt>{labels.vehicleLabel}</dt>
              <dd dir="ltr">{details.vehicleSummary.trim()}</dd>
            </div>
          ) : null}
          <div className={styles.detailRow}>
            <dt>{labels.bookingIdLabel}</dt>
            <dd>
              <code className={styles.id}>{bookingId || '—'}</code>
            </dd>
          </div>
        </dl>
        <BookingCalendarExportButtons event={calendarEvent} />
        {!qrOpen ? (
          <button
            type="button"
            className={styles.showBarcodeButton}
            onClick={() => setQrOpen(true)}
          >
            <BarcodeIcon />
            <span>{labels.showBarcode}</span>
          </button>
        ) : (
          <div className={styles.qrBlock}>
            {qrDataUrl ? (
              <img className={styles.qrImage} src={qrDataUrl} width={220} height={220} alt="" />
            ) : (
              <p className={styles.qrLoading}>{labels.loading ?? '…'}</p>
            )}
            <p className={styles.qrCaption}>{labels.qrCaption}</p>
            <button
              type="button"
              className={styles.hideBarcodeButton}
              onClick={() => setQrOpen(false)}
            >
              {labels.hideBarcode}
            </button>
          </div>
        )}
      </section>
    </div>,
    document.body,
  );
}
